import { Box, Flex, Grid, GridItem, Image, Input, InputGroup, InputLeftElement, Select, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel, Button, IconButton, Tooltip, } from "@chakra-ui/react";
import MyDiv from "./bankName.style";
import { ChevronDownIcon, EditIcon, Search2Icon } from "@chakra-ui/icons";
import { AlertCard, CustomButton, CustomToast } from "../../../../components";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import { useEffect, useState } from "react";
import PlusIcon from "../../../../assets/images/Vector.svg";
import { useFetch } from '../../../../hooks/useFetch';
import { IBank } from './model';

const BankName = () => {
  const [AlertOpen, setAlertOpen] = useState(false);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBank, setCurrentBank] = useState({ id: 0, bank_Name: "", status: true, });
  const [statusChangeSource, setStatusChangeSource] = useState<"table" | "drawer" | null>(null);
  const [banks, setBanks] = useState<IBank[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);

  const [dataLoading, setDataLoading] = useState(false);
  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0,
    rows: 20,
    sortField: undefined,
    sortOrder: undefined,
    multiSortMeta: [],
    filters: {},
    globalFilter: '',
  });

  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource("table");
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource("drawer");
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedId !== null && pendingStatus !== null) {
      const bankId = statusChangeSource === "table" ? selectedId : currentBank.id;

      const response = await fetchApi(`Hrlense_Bank/toggleStatus/${bankId}`, "PUT", { id: bankId, status: pendingStatus }, null, "Bank Status updated successfully"); // Changed toast message for clarity
      if (response) {
        await loadBankTable();
        if (statusChangeSource === "drawer") {
          setCurrentBank((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
  };


  const handleAddNewBank = () => {
    setIsEditMode(false);
    setCurrentBank({ id: 0, bank_Name: "", status: true, });
    setDrawerOpen(true);
  };

  const handleEditBank = (bank: IBank) => {
    setIsEditMode(true);
    setSelectedId(bank.id);
    setCurrentBank(bank);
    setDrawerOpen(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    if (!/^[A-Za-z\s]*$/.test(value)) {
      return;
    }
    setCurrentBank(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!currentBank.bank_Name.trim()) {
      addToast({ message: "Please enter Bank Name", status: "warning" });
      return;
    }
    if (isEditMode) {
      const response = await fetchApi(`Hrlense_Bank/${currentBank.id}`, 'PUT', { id: currentBank.id, bank_Name: currentBank.bank_Name, status: currentBank.status }, null, "Bank Name updated successfully");
      if (response) {
        await loadBankTable();
        setDrawerOpen(false);
      }
    } else {
      const response = await fetchApi<IBank>('Hrlense_Bank', 'POST', { bank_Name: currentBank.bank_Name, status: currentBank.status }, null, "Bank Name added successfully");
      if (response) {
        await loadBankTable();
        setDrawerOpen(false);
      }
    }
  };

  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadBankTable();
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [lazyParams]);


  const loadBankTable = async () => {
    setDataLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('first', String(lazyParams.first));
      params.set('rows', String(lazyParams.rows));
      if (lazyParams.sortField) params.set('sortField', lazyParams.sortField);
      if (lazyParams.sortOrder) params.set('sortOrder', String(lazyParams.sortOrder));
      if (lazyParams.globalFilter) params.set('filter', lazyParams.globalFilter);
      if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
        params.set('filters', JSON.stringify(lazyParams.filters));
      }

      const response = await fetchApi<{ banks: IBank[]; totalCount: number }>(`Hrlense_Bank?${params.toString()}`, 'GET');
      if (response) {
        setBanks(response.banks || []);
        setTotalCount(response.totalCount ?? 0);
      }
    } catch (error) {
      addToast({ message: "Failed to load bank data.", status: "error" });
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <MyDiv>
      <Flex className="page_heading flex_header">
        <Text className="font-poppins text_semibold text_3xl">Bank Master</Text>
        <Flex gap={2}>
          <CustomButton title="Add New Bank Name" className=" btn font-poppins text_sm text_medium" rightIcon={<Image src={PlusIcon} className="logo" />} onClick={handleAddNewBank} />
        </Flex>
      </Flex>
      <Grid className="grid_container">
        <GridItem colSpan={12}>
          <CustomTables<IBank>
            value={banks} loading={dataLoading} emptyMessage="No banks found." preferenceKey="bankTable" showGlobalSearch={false} showToolbar={true}
            lazy lazyParams={lazyParams} onLazyParamsChange={setLazyParams} totalRecords={totalCount} showPagination
            rowsPerPageOptions={[10, 20, 50, 100]} maxTableHeight={null}
          >
            <Column header="S. No" body={(_, index) => lazyParams.first + index + 1} />
            <Column header="Bank Name" field="bank_Name" sortable filterable filterType="text" />
            <Column header="Bank Status" field="status" filterable filterType="boolean" body={(bank: IBank) => (
              <Select value={bank.status ? "true" : "false"} onChange={(e) => handleStatusChange(bank.id, e.target.value === "true")}
                width="120px" height="32px" icon={<ChevronDownIcon />} borderRadius="4px"
                color={getStatusColor(bank.status)} className="font-poppins text_medium text_xsm"
              >
                <option value="true" style={{ color: "#38A169" }}>Active</option>
                <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
              </Select>
            )}
            />
            <Column header="Action" frozen="right" body={(bank: IBank) => (
              <Tooltip label="Edit Bank" hasArrow>
                <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditBank(bank)} />
              </Tooltip>
            )}
            />
          </CustomTables>

        </GridItem>
      </Grid>
      <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />
      <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">{isEditMode ? "Edit Bank" : "Add New Bank"}</DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Box py={4}>
              <FormControl mb={4} isRequired>
                <FormLabel>Bank Name</FormLabel>
                <Input name="bank_Name" value={currentBank.bank_Name} onChange={handleInputChange} placeholder="Enter bank name" />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel>Status</FormLabel>
                <Select w="120px" value={currentBank.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentBank.status)}>
                  <option value="true" style={{ color: "#38A169" }}> Active</option>
                  <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                </Select>
              </FormControl>
            </Box>
            <Flex justify="flex-end" mt={4} gap={2}>
              <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "#D92F34", color: "white" }} >Cancel</Button>
              <Button onClick={handleSubmit} className="font-poppins text_sm text_medium" style={{ backgroundColor: "#006838", color: "white" }} isLoading={loading} >{isEditMode ? "Update Bank" : "Add Bank"}</Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </MyDiv>
  );
};
export default BankName;